import React, { ChangeEvent, useEffect, useRef, useState } from 'react';

import UploadIcon from '@src/components/Icon/UploadIcon';
import { themeStyle } from '@src/theming';

import {
  Container,
  ImageContainer,
  TextContainer,
  Text,
  Image,
  DragOverlay,
} from './styles';
import { defaultImageInputTheme } from './theme';
import { DragState, ImageInputProps } from './types';
import { getFirstFileFromDataTransfer, validateDataTransfer } from './utils';

const ImageInput: React.FC<ImageInputProps> = ({
  allowedMimeTypes = ['image/png', 'image/jpeg'],
  onChange,
  disabled,
  selectedImage,
  theme,
}) => {
  const [dragState, setDragState] = useState<DragState>(DragState.Default);
  const [imageUrl, setImageUrl] = useState('');

  const ImageInputRef = useRef<HTMLInputElement>(null);
  const resetImageInputValue = () => {
    if (ImageInputRef.current) {
      ImageInputRef.current.value = '';
    }
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const files = Array.from(event.target.files ?? []);
    if (!files.length) {
      resetImageInputValue();
      return;
    }

    const result = files[0];

    if (onChange) {
      onChange(result);
    }

    resetImageInputValue();
  };

  const handleDrop: React.DragEventHandler = (event) => {
    event.preventDefault();

    if (disabled) {
      return;
    }

    setDragState(DragState.Default);

    const isValid = validateDataTransfer(event.dataTransfer, allowedMimeTypes);

    if (!isValid) {
      return;
    }

    const file = getFirstFileFromDataTransfer(event.dataTransfer);
    if (file && onChange) {
      onChange(file);
    }
  };

  const handleDragEnter: React.DragEventHandler = (event) => {
    event.preventDefault();

    if (disabled) {
      return;
    }

    const isValid = validateDataTransfer(event.dataTransfer, allowedMimeTypes);

    setDragState(isValid ? DragState.Valid : DragState.Invalid);
  };

  const handleDragLeave: React.DragEventHandler = (event) => {
    event.preventDefault();

    setDragState(DragState.Default);
  };

  const handleDragOver: React.DragEventHandler = (event) => {
    event.preventDefault();

    if (disabled) {
      return;
    }

    const isValid = validateDataTransfer(event.dataTransfer, allowedMimeTypes);

    setDragState(isValid ? DragState.Valid : DragState.Invalid);

    event.dataTransfer.dropEffect = 'copy';
  };

  const openFilePicker = (): void => {
    if (disabled) {
      return;
    }

    ImageInputRef.current?.click();
  };

  useEffect(() => {
    if (!selectedImage) {
      setImageUrl('');
      return;
    }

    if (typeof selectedImage === 'string') {
      setImageUrl(selectedImage);
      return;
    }

    const fileUrl = URL.createObjectURL(selectedImage);
    setImageUrl(fileUrl);

    return () => {
      if (fileUrl) {
        URL.revokeObjectURL(fileUrl);
      }
    };
  }, [selectedImage]);

  const $theme = { ...defaultImageInputTheme, ...theme };

  return (
    <Container $theme={$theme} className={dragState} disabled={disabled}>
      {/*The overlay is needed to prevent children from triggering dragleave.*/}
      <DragOverlay
        onClick={openFilePicker}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      />
      {!!imageUrl && (
        <ImageContainer $theme={$theme}>
          <Image alt="Uploaded image" src={imageUrl} />
        </ImageContainer>
      )}
      <TextContainer>
        <UploadIcon
          ignoreHoverStyles
          height="20px"
          theme={{ color: themeStyle.varInkMedium }}
        />
        <Text>
          Drag and drop or <span>upload image</span>
        </Text>
      </TextContainer>
      <input
        ref={ImageInputRef}
        hidden
        accept={allowedMimeTypes.join(', ')}
        disabled={disabled}
        multiple={false}
        type="file"
        onChange={handleInputChange}
      />
    </Container>
  );
};

export default ImageInput;
